import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, List, Typography, Tag, Empty, Badge, Tooltip } from "antd";
import {
  TrophyOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  ReadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { getUserAchievementsAction } from "@redux/actions/gamification/achievementActions";
import moment from "moment";

const { Text } = Typography;

const AchievementsCard = () => {
  const dispatch = useDispatch();
  const { achievements } = useSelector((state) => state.achievement);

  useEffect(() => {
    dispatch(getUserAchievementsAction());
  }, [dispatch]);

  // Define achievement icons by type with Chinese app-style colors
  const achievementIcons = {
    referral_first: <TeamOutlined style={{ color: "white" }} />,
    referral_milestone: <TeamOutlined style={{ color: "white" }} />,
    workflow_created: <RocketOutlined style={{ color: "white" }} />,
    tutorial_completed: <ReadOutlined style={{ color: "white" }} />,
  };

  const getAchievementGradient = (type, index) => {
    const gradients = [
      "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)", // Purple
      "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)", // Pink
      "linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)", // Purple to Pink
      "linear-gradient(135deg, #A855F7 0%, #8B5CF6 100%)", // Light Purple
      "linear-gradient(135deg, #F472B6 0%, #EC4899 100%)", // Light Pink
      "linear-gradient(135deg, #C084FC 0%, #A855F7 100%)", // Violet
      "linear-gradient(135deg, #E879F9 0%, #D946EF 100%)", // Fuchsia
    ];
    return gradients[index % gradients.length];
  };

  return (
    <Card
      style={{
        background: "white",
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      bodyStyle={{
        padding: "0",
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <TrophyOutlined
              style={{
                color: "#8B5CF6",
                fontSize: "20px",
                marginRight: "12px",
              }}
            />
            <Text
              style={{
                color: "#262626",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Achievements
            </Text>
          </div>
          {achievements?.totalEarned > 0 && (
            <Badge
              count={achievements.totalEarned}
              style={{
                backgroundColor: "#52c41a",
                color: "white",
                border: "none",
                fontSize: "11px",
                fontWeight: "500",
              }}
            />
          )}
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {achievements?.achievements &&
          achievements.achievements.length > 0 ? (
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                paddingRight: "4px",
              }}
            >
              {achievements.achievements.map((achievement, index) => (
                <div
                  key={achievement.id || index}
                  style={{
                    background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "12px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Achievement item */}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        background: getAchievementGradient(
                          achievement.type,
                          index
                        ),
                        width: "44px",
                        height: "44px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        marginRight: "12px",
                        position: "relative",
                      }}
                    >
                      {achievementIcons[achievement.type] || (
                        <TrophyOutlined style={{ color: "white" }} />
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "4px",
                        }}
                      >
                        <Text
                          style={{
                            color: "#262626",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          {achievement.name}
                        </Text>

                        {achievement.creditsEarned > 0 && (
                          <div
                            style={{
                              background: "#52c41a",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Text
                              style={{
                                color: "white",
                                fontSize: "11px",
                                fontWeight: "500",
                              }}
                            >
                              +{achievement.creditsEarned}
                            </Text>
                          </div>
                        )}
                      </div>

                      <Text
                        style={{
                          fontSize: "12px",
                          color: "#8c8c8c",
                          lineHeight: "1.4",
                        }}
                      >
                        {achievement.description}
                      </Text>

                      <Text
                        style={{
                          fontSize: "10px",
                          color: "#bfbfbf",
                          marginTop: "4px",
                          display: "block",
                        }}
                      >
                        {moment(achievement.earnedAt).format("MMM DD, YYYY")}
                      </Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)",
                borderRadius: "12px",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <TrophyOutlined style={{ color: "white", fontSize: "24px" }} />
              </div>
              <Text
                style={{
                  color: "#262626",
                  fontSize: "16px",
                  fontWeight: "600",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                No achievements yet
              </Text>
              <Text
                style={{
                  fontSize: "12px",
                  color: "#8c8c8c",
                }}
              >
                Complete tasks to earn achievements and credits!
              </Text>
            </div>
          )}

          {achievements?.totalEarned > 0 && (
            <div
              style={{
                textAlign: "center",
                marginTop: "16px",
                padding: "12px",
                background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)",
                borderRadius: "12px",
              }}
            >
              <Text
                style={{
                  fontSize: "12px",
                  color: "#595959",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleOutlined
                  style={{ marginRight: "6px", color: "#52c41a" }}
                />
                You've earned {achievements.creditsEarned} credits total!
                <TrophyOutlined
                  style={{ marginLeft: "6px", color: "#faad14" }}
                />
              </Text>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AchievementsCard;
